import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Calendar, Star, MapPin, Wrench } from "lucide-react";
import { toast } from "sonner";
import { hsTrades, hsSearch, hsProDetail, hsSlots, hsBook, hsMyBookings, hsCancelBooking, hsReviews } from "../lib/api";

const TRADE_LABELS = {
  serrurier: "Serrurier", plombier: "Plombier", coiffeur: "Coiffeur à domicile",
  depanneur: "Dépanneur", electricien: "Électricien",
};
const ST_LABEL = {
  pending: "En attente", accepted: "Confirmé", refused: "Refusé", cancelled: "Annulé", completed: "Terminé",
};

function nextDays(n) {
  const out = [];
  const names = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];
  for (let i = 0; i < n; i++) {
    const d = new Date(); d.setDate(d.getDate() + i);
    out.push({ iso: d.toISOString().slice(0, 10), label: `${names[d.getDay()]} ${d.getDate()}/${d.getMonth() + 1}` });
  }
  return out;
}

export default function HomeServices() {
  const nav = useNavigate();
  const [view, setView] = useState("list");
  const [trades, setTrades] = useState([]);
  const [trade, setTrade] = useState(null);
  const [commune, setCommune] = useState("");
  const [sort, setSort] = useState("default");
  const [pros, setPros] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pro, setPro] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [day, setDay] = useState(nextDays(14)[0].iso);
  const [slots, setSlots] = useState([]);
  const [booking, setBooking] = useState(false);
  const [mine, setMine] = useState([]);

  const runSearch = useCallback(async () => {
    setLoading(true);
    try { setPros(await hsSearch(trade || undefined, commune.trim() || undefined, sort === "rating" ? "rating" : undefined)); }
    catch { setPros([]); }
    finally { setLoading(false); }
  }, [trade, commune, sort]);

  useEffect(() => { hsTrades().then(setTrades).catch(() => {}); runSearch(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (view === "list") runSearch(); }, [trade, sort]); // eslint-disable-line

  const loadSlots = async (proId, d) => {
    setSlots([]);
    try { setSlots(await hsSlots(proId, d)); } catch { setSlots([]); }
  };

  const openPro = async (p) => {
    try {
      const detail = await hsProDetail(p.id);
      setPro(detail); setView("pro");
      loadSlots(p.id, day);
      hsReviews(p.id).then((r) => setReviews(r.reviews || [])).catch(() => setReviews([]));
    } catch { toast.error("Professionnel indisponible"); }
  };

  const chooseDay = (d) => { setDay(d); if (pro) loadSlots(pro.id, d); };

  const confirmBook = async (start, serviceId) => {
    if (!window.confirm(`Demander ce rendez-vous ?\n${start.replace("T", " à ")}`)) return;
    setBooking(true);
    try {
      await hsBook({ pro_id: pro.id, service_id: serviceId, start, commune: commune.trim() || undefined });
      toast.success("Demande envoyée — le professionnel va confirmer.");
      loadSlots(pro.id, day);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Créneau indisponible.");
    } finally { setBooking(false); }
  };

  const loadMine = async () => {
    try { setMine(await hsMyBookings()); setView("mine"); }
    catch { toast.error("Connexion requise pour voir vos rendez-vous."); }
  };

  const cancel = async (id) => {
    if (!window.confirm("Annuler ce rendez-vous ?")) return;
    await hsCancelBooking(id); loadMine();
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-5">
        <button onClick={() => (view === "list" ? nav("/") : setView("list"))} data-testid="hs-back"
          className="w-10 h-10 rounded-full flex items-center justify-center" style={{ border: "1px solid #e5e7eb" }}>
          <ChevronLeft size={22} color="#075a43" />
        </button>
        <h1 className="text-xl font-extrabold" style={{ color: "#075a43" }}>Santé &amp; Services à domicile</h1>
        <button onClick={loadMine} data-testid="hs-mine"
          className="w-10 h-10 rounded-full flex items-center justify-center" style={{ border: "1px solid #e5e7eb" }}>
          <Calendar size={20} color="#075a43" />
        </button>
      </div>

      {view === "list" && (
        <>
          <div className="flex gap-2 overflow-x-auto pb-3 mb-3">
            <button onClick={() => setTrade(null)} data-testid="hs-trade-all"
              className="whitespace-nowrap px-4 py-2 rounded-full text-sm font-bold"
              style={{ background: !trade ? "#2d6a4f" : "#fff", color: !trade ? "#fff" : "#0b1220", border: "1px solid #e5e7eb" }}>
              Tous
            </button>
            {trades.map((t) => (
              <button key={t} onClick={() => setTrade(t)} data-testid={`hs-trade-${t}`}
                className="whitespace-nowrap px-4 py-2 rounded-full text-sm font-bold"
                style={{ background: trade === t ? "#2d6a4f" : "#fff", color: trade === t ? "#fff" : "#0b1220", border: "1px solid #e5e7eb" }}>
                {TRADE_LABELS[t] || t}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 mb-3 bg-white rounded-xl px-3" style={{ border: "1px solid #e5e7eb" }}>
            <MapPin size={18} color="#64748b" />
            <input value={commune} onChange={(e) => setCommune(e.target.value)} data-testid="hs-commune"
              onKeyDown={(e) => e.key === "Enter" && runSearch()}
              placeholder="Commune (ex : Payerne)" className="flex-1 h-11 outline-none text-sm" />
            <button onClick={runSearch} data-testid="hs-search"
              className="px-4 py-2 rounded-lg text-white text-sm font-bold" style={{ background: "#2d6a4f" }}>
              Rechercher
            </button>
          </div>

          <div className="flex gap-2 mb-5">
            <button onClick={() => setSort("default")} data-testid="hs-sort-default"
              className="px-4 py-2 rounded-full text-sm font-bold"
              style={{ background: sort === "default" ? "#075a43" : "#fff", color: sort === "default" ? "#fff" : "#0b1220", border: "1px solid #e5e7eb" }}>
              Pertinence
            </button>
            <button onClick={() => setSort("rating")} data-testid="hs-sort-rating"
              className="px-4 py-2 rounded-full text-sm font-bold"
              style={{ background: sort === "rating" ? "#075a43" : "#fff", color: sort === "rating" ? "#fff" : "#0b1220", border: "1px solid #e5e7eb" }}>
              ★ Mieux notés
            </button>
          </div>

          {pros.length === 0 && !loading && (
            <p className="text-center py-10" style={{ color: "#94a3b8" }}>Aucun professionnel référencé pour ce filtre.</p>
          )}
          {pros.map((p) => (
            <button key={p.id} onClick={() => openPro(p)} data-testid={`hs-pro-${p.id}`}
              className="w-full flex items-center gap-3 bg-white rounded-2xl p-4 mb-3 text-left" style={{ border: "1px solid #e5e7eb" }}>
              <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: "#1b4332" }}>
                <Wrench size={20} color="#fff" />
              </div>
              <div className="flex-1">
                <p className="font-bold" style={{ color: "#0b1220" }}>{p.business_name}</p>
                <p className="text-sm" style={{ color: "#64748b" }}>
                  {(TRADE_LABELS[p.trade] || p.trade)} · {(p.communes || []).join(", ")}
                </p>
                {p.rating_count > 0 && (
                  <p className="text-sm font-bold mt-1" style={{ color: "#f97316" }}>
                    ★ {Number(p.rating_avg).toFixed(1)} ({p.rating_count})
                  </p>
                )}
              </div>
            </button>
          ))}
        </>
      )}

      {view === "pro" && pro && (
        <div>
          <h2 className="text-2xl font-extrabold" style={{ color: "#075a43" }}>{pro.business_name}</h2>
          <p className="text-sm" style={{ color: "#64748b" }}>
            {(TRADE_LABELS[pro.trade] || pro.trade)} · {(pro.communes || []).join(", ")}
          </p>
          {pro.rating_count > 0 && (
            <p className="text-sm font-bold mt-1" style={{ color: "#f97316" }}>
              ★ {Number(pro.rating_avg).toFixed(1)} · {pro.rating_count} avis
            </p>
          )}
          {!!pro.description && <p className="mt-3" style={{ color: "#0b1220" }}>{pro.description}</p>}

          {(pro.services || []).length > 0 && <h3 className="text-lg font-extrabold mt-6 mb-2" style={{ color: "#075a43" }}>Prestations</h3>}
          {(pro.services || []).map((sv) => (
            <div key={sv.id} className="flex justify-between py-3" style={{ borderBottom: "1px solid #eef2f7" }}>
              <span className="font-semibold" style={{ color: "#0b1220" }}>{sv.name}</span>
              <span className="font-bold" style={{ color: "#2d6a4f" }}>{sv.price_chf ? `dès CHF ${Number(sv.price_chf).toFixed(2)}` : ""}</span>
            </div>
          ))}

          {reviews.length > 0 && <h3 className="text-lg font-extrabold mt-6 mb-2" style={{ color: "#075a43" }}>Avis clients</h3>}
          {reviews.map((rv) => (
            <div key={rv.id} className="bg-white rounded-xl p-3 mb-2" style={{ border: "1px solid #e5e7eb" }}>
              <p style={{ color: "#f97316", fontWeight: 800 }}>{"★".repeat(rv.rating)}{"☆".repeat(5 - rv.rating)}</p>
              {!!rv.comment && <p className="mt-1 text-sm" style={{ color: "#0b1220" }}>{rv.comment}</p>}
              {!!rv.pro_response && (
                <div className="mt-2 pt-2 rounded-lg p-2" style={{ borderTop: "1px solid #eef2f7", background: "rgba(7,90,67,0.05)" }}>
                  <p className="text-xs font-bold uppercase" style={{ color: "#075a43" }}>Réponse du professionnel</p>
                  <p className="text-sm mt-1" style={{ color: "#0b1220" }}>{rv.pro_response}</p>
                </div>
              )}
            </div>
          ))}

          <h3 className="text-lg font-extrabold mt-6 mb-2" style={{ color: "#075a43" }}>Choisir une date</h3>
          <div className="flex gap-2 overflow-x-auto pb-3">
            {nextDays(14).map((d) => (
              <button key={d.iso} onClick={() => chooseDay(d.iso)} data-testid={`hs-day-${d.iso}`}
                className="whitespace-nowrap px-4 py-2 rounded-full text-sm font-bold"
                style={{ background: day === d.iso ? "#2d6a4f" : "#fff", color: day === d.iso ? "#fff" : "#0b1220", border: "1px solid #e5e7eb" }}>
                {d.label}
              </button>
            ))}
          </div>

          <h3 className="text-lg font-extrabold mt-4 mb-2" style={{ color: "#075a43" }}>Créneaux disponibles</h3>
          {slots.length === 0 && <p style={{ color: "#94a3b8" }}>Aucun créneau libre ce jour.</p>}
          <div className="flex flex-wrap gap-2">
            {slots.map((sl) => (
              <button key={sl} disabled={booking} onClick={() => confirmBook(sl, (pro.services || [])[0]?.id)} data-testid={`hs-slot-${sl}`}
                className="px-4 py-3 rounded-xl font-bold" style={{ border: "1px solid #2d6a4f", color: "#075a43", background: "rgba(7,90,67,0.06)" }}>
                {sl.slice(11, 16)}
              </button>
            ))}
          </div>
        </div>
      )}

      {view === "mine" && (
        <div>
          <h3 className="text-lg font-extrabold mb-3" style={{ color: "#075a43" }}>Mes rendez-vous</h3>
          {mine.length === 0 && <p style={{ color: "#94a3b8" }}>Aucun rendez-vous.</p>}
          {mine.map((b) => (
            <div key={b.id} className="flex items-center justify-between bg-white rounded-2xl p-4 mb-3" style={{ border: "1px solid #e5e7eb" }}>
              <div>
                <p className="font-bold" style={{ color: "#0b1220" }}>{b.service_name || "Rendez-vous"}</p>
                <p className="text-sm" style={{ color: "#64748b" }}>{b.start.replace("T", " à ")} · {ST_LABEL[b.status] || b.status}</p>
              </div>
              {(b.status === "pending" || b.status === "accepted") && (
                <button onClick={() => cancel(b.id)} data-testid={`hs-cancel-${b.id}`}
                  className="px-3 py-2 rounded-lg text-sm font-bold" style={{ border: "1px solid #f97316", color: "#f97316" }}>
                  Annuler
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
